import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useNavigate, Link } from "react-router-dom";

function OfferList() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // ✅ 3 modes
  const [view, setView] = useState("active"); // "active" | "inactive" | "brandInactive"

  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("profileToken");
  const headers = { "x-access-token": token || "" };

  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseURL}/offer/list`, { headers });
      setOffers(res.data.result || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setMessage("Error fetching offers");
      setLoading(false);
    }
  };

  // ✅ counts
  const activeCount = useMemo(
    () => offers.filter((o) => Number(o.status) === 1 && Number(o.brand_status) === 1).length,
    [offers]
  );

  const inactiveCount = useMemo(
    () => offers.filter((o) => Number(o.status) === 0).length,
    [offers]
  );

  const brandInactiveCount = useMemo(
    () => offers.filter((o) => Number(o.status) === 1 && Number(o.brand_status) === 0).length,
    [offers]
  );


  // ✅ filtered list by view
  const visibleOffers = useMemo(() => {
    if (view === "inactive") {
      return offers.filter((o) => Number(o.status) === 0);
    }
    if (view === "brandInactive") {
      return offers.filter((o) => Number(o.status) === 1 && Number(o.brand_status) === 0);
    }
    // ✅ active view: personal active + brand active
    return offers.filter((o) => Number(o.status) === 1 && Number(o.brand_status) === 1);
  }, [offers, view]);


  const deleteOffer = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${baseURL}/offer/delete/${id}`, { headers });
      Swal.fire("Deleted!", "Offer has been deleted.", "success");
      fetchOffers();
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Something went wrong.", "error");
    }
  };

  const handleToggleStatus = async (offer) => {
    try {
      await axios.patch(`${baseURL}/offer/${offer.id}/status`, {}, { headers });

      const newStatus = Number(offer.status) === 1 ? 0 : 1;

      setOffers((prev) =>
        prev.map((x) => (x.id === offer.id ? { ...x, status: newStatus } : x))
      );

      // ✅ view switch logic
      if (newStatus === 0) setView("inactive");
      else setView(Number(offer.brand_status) === 0 ? "brandInactive" : "active");

      Swal.fire("Success", "Status updated", "success");
    } catch (err) {
      Swal.fire("Error!", "Could not update status.", "error");
    }
  };


  if (loading) return <p className="text-center mt-5">Loading offers...</p>;

  const heading =
    view === "inactive"
      ? "Inactive Offers"
      : view === "brandInactive"
        ? "Brand Inactive Offers"
        : "Offer List";

  const badgeCount =
    view === "inactive"
      ? inactiveCount
      : view === "brandInactive"
        ? brandInactiveCount
        : activeCount;

  return (
    <div className="container my-5 p-4 border rounded shadow bg-white">
      {/* ✅ header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0 text-success">
          {heading}{" "}
          <span className="badge bg-secondary ms-2">{badgeCount}</span>
        </h3>

        <div className="d-flex gap-2 flex-wrap">
          {/* ✅ Active/Inactive toggle (personal status) */}
          <button
            className={`btn ${view === "inactive" ? "btn-outline-success" : "btn-outline-danger"
              } rounded-pill px-4`}
            onClick={() => setView((p) => (p === "inactive" ? "active" : "inactive"))}
            title="Toggle Active/Inactive offers"
          >
            {view === "inactive"
              ? `Active Offers (${activeCount})`
              : `Inactive Offers (${inactiveCount})`}
          </button>

          {/* ✅ Brand Inactive offers */}
          <button
            className={`btn ${view === "brandInactive" ? "btn-warning" : "btn-outline-warning"
              } rounded-pill px-4`}
            onClick={() => setView((p) => (p === "brandInactive" ? "active" : "brandInactive"))}
            title="Offers whose brand is inactive"
          >
            Brand Inactive ({brandInactiveCount})
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/add-offer">
            <i className="fas fa-plus me-2"></i> Add Offer
          </Link>
        </div>
      </div>

      {/* ✅ top note only for brandInactive view */}
      {view === "brandInactive" && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            These offers are <b>not visible on website</b> because their brands are inactive.
          </div>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => navigate("/brands-list?view=inactive")}
          >
            Go to Inactive Brands
          </button>
        </div>
      )}

      {message && <div className="alert alert-info text-center">{message}</div>}

      <table className="table table-striped table-bordered align-middle text-center">
        <thead className="table-success">
          <tr>
            <th>#</th>
            <th>Text</th>
            <th>Tag</th>
            <th>Phone No</th>
            <th>Brand</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {visibleOffers.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-4 text-muted">
                No offers found.
              </td>
            </tr>
          ) : (
            visibleOffers.map((offer, index) => {
              const isBrandInactive = Number(offer.brand_status) === 0;

              return (
                <tr
                  key={offer.id}
                  className={
                    view === "inactive"
                      ? "table-secondary"
                      : view === "brandInactive"
                        ? "table-warning"
                        : ""
                  }
                  style={view !== "active" ? { opacity: 0.95 } : {}}
                >
                  <td>{index + 1}</td>

                  <td style={{ textAlign: "left" }}>
                    <div className="fw-semibold">{offer.text}</div>

                    {view === "inactive" && (
                      <span className="badge bg-danger mt-1">Inactive</span>
                    )}

                    {/* ✅ row note only if brand inactive */}
                    {isBrandInactive && (
                      <div className="small text-danger mt-1">
                        For activate this offer you should activate{" "}
                        <b>{offer.brand_name || "this brand"}</b>.
                      </div>
                    )}
                  </td>

                  <td>{offer.offer_tag || "-"}</td>
                  <td>{offer.phone_no || "-"}</td>

                  <td>
                    {offer.brand_name || "-"}
                    {isBrandInactive && (
                      <span className="badge bg-danger ms-2">Brand Inactive</span>
                    )}
                  </td>

                  <td>
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Number(offer.status) === 1}
                        onChange={() => handleToggleStatus(offer)}
                      />
                    </div>
                  </td>

                  <td>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      title="Edit"
                      onClick={() => navigate(`/update-offer/${offer.id}`)}
                    >
                      <i className="fa fa-edit"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      title="Delete"
                      onClick={() => deleteOffer(offer.id)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default OfferList;
